/* eslint-disable */
import React, { useRef } from "react";
import PropTypes from "prop-types";
import { ThemeProvider } from "@material-ui/styles";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import theme from "../../../../Themes/defaultTheme";
import A_FormHelper from "../../DataDisplay/Tooltips/A_FormHelper";
import { asyncCheckbox } from "../../../Molecules/Common/Renderers/M_FormRendererHelper";

import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    "& [class*='MuiInputBase-root']": {
      color: "var(--color-text-primary) !important",
    },
    "& [class*='MuiOutlinedInput-root']": {
      backgroundColor: "var(--color-background-paper)",
      borderRadius: "calc(10px * var(--app-radius-scale, 1))",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "& fieldset": {
        borderColor: "var(--total_row_boldbackground)",
      },
      "&:not(.Mui-error):hover fieldset": {
        borderColor: "var(--color-primary-main)",
      },
      "&.Mui-focused:not(.Mui-error) fieldset": {
        borderColor: "var(--color-primary-main)",
      },
      "&.Mui-disabled": {
        backgroundColor: "var(--total_row_background)",
        "& fieldset": {
          borderColor: "var(--total_row_boldbackground) !important",
        },
      },
    },
    "& [class*='MuiInput-underline']:before": {
      borderBottomColor: "var(--total_row_boldbackground)",
    },
    "& [class*='MuiInput-underline']:hover:not(.Mui-disabled):before": {
      borderBottomColor: "var(--color-primary-main)",
    },
    "& [class*='MuiInput-underline']:not(.Mui-error):after": {
      borderBottomColor: "var(--color-primary-main)",
    },
    "& [class*='MuiInput-underline'].Mui-disabled:before": {
      borderBottomColor: "var(--total_row_boldbackground)",
      borderBottomStyle: "dotted",
    },
    "& [class*='MuiInputLabel-root']:not(.Mui-error)": {
      color: "var(--color-text-secondary)",
      "&.Mui-focused": {
        color: "var(--color-primary-text)",
      },
      "&.Mui-disabled": {
        color: "var(--color-text-secondary)",
      },
    },
    "& [class*='MuiFormHelperText-root']:not(.Mui-error)": {
      color: "var(--color-text-secondary)",
    },
    "& [class*='MuiInputBase-input']": {
      color: "var(--color-text-primary) !important",
      "&::placeholder": {
        color: "var(--color-text-secondary)",
        opacity: 0.7,
      },
    },
    "& [class*='MuiInputBase-root'].Mui-disabled": {
      color: "var(--color-text-secondary) !important",
    },
    "& [class*='MuiInputBase-input'].Mui-disabled": {
      color: "var(--color-text-secondary) !important",
      WebkitTextFillColor: "var(--color-text-secondary) !important",
      cursor: "not-allowed",
      opacity: 1,
    },
    "& [class*='MuiInputBase-root'].Mui-disabled [class*='MuiInputAdornment-root']":
      {
        color: "var(--color-text-secondary)",
      },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  },
  spinnerWrap: {
    display: "flex",
    flexDirection: "column",
    marginRight: -6,
  },
  spinnerBtn: {
    padding: 0,
    width: 20,
    height: 14,
    borderRadius: "calc(2px * var(--app-radius-scale, 1))",
    color: "var(--color-text-secondary)",
    "&:hover": {
      backgroundColor: "transparent",
      color: "var(--color-primary-text)",
    },
  },
  spinnerIcon: {
    fontSize: "1.125rem",
  },
}));

/**
 * Truncates (never rounds) the fractional part of a numeric input string to `maxDecimals`
 * digits. Rounding would silently change the number the user is halfway through typing.
 */
const capDecimalPlaces = (value, maxDecimals) => {
  const text = String(value);
  const point = text.indexOf(".");
  if (point === -1) return text;
  return maxDecimals > 0 ? text.slice(0, point + 1 + maxDecimals) : text.slice(0, point);
};

const A_TextField = (props) => {
  const classes = useStyles();
  const formVlalueType = props.inputType === "value" ? "value" : "defaultValue";
  const defaultVal = props.defaultValue || "";
  const maxLength = props.inputMaxLength || null;
  const maxDecimals = Number.isInteger(props.inputMaxDecimals) && props.inputMaxDecimals >= 0 ? props.inputMaxDecimals : null;
  const isNumberType = props.type === "number";

  const inputLabelProps =
    props.id === asyncCheckbox.Rationale
      ? {
          style: {
            display: "-webkit-box",
            width: "50ch",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            bottom: 19,
          },
          shrink: true,
        }
      : props.InputLabelProps || {};

  const handleChange = (event) => {
    let { value } = event.target;
    if (maxLength && value.length > maxLength) {
      value = value.slice(0, maxLength);
      event.target.value = value;
    }
    // Cap the fraction as it is typed or pasted, rather than only complaining on save: without
    // this a 2dp money field happily accepts "10.4444444", which the API then rejects. The
    // truncated string is written back into the DOM input because most callers use this atom
    // UNCONTROLLED (defaultValue) and would otherwise show digits their own state no longer has.
    if (maxDecimals !== null) {
      const capped = capDecimalPlaces(value, maxDecimals);
      if (capped !== value) {
        value = capped;
        event.target.value = capped;
      }
    }
    if (props.onChange) {
      props.onChange(event);
    }
  };

  // Most callers use this atom UNCONTROLLED (`defaultValue`, the app-wide convention), and React
  // only applies defaultValue on mount — so the spinner buttons have to write the stepped value
  // into the DOM input themselves. Without this they fired onChange (updating the caller's state)
  // while the box on screen never moved, which is why the arrows looked dead. Kept as a merged
  // ref so a caller's own inputRef still works.
  const inputNodeRef = useRef(null);
  const setInputRef = (node) => {
    inputNodeRef.current = node;
    if (typeof props.inputRef === "function") props.inputRef(node);
    else if (props.inputRef) props.inputRef.current = node;
  };

  const stepNumericValue = (direction) => {
    const step = parseFloat(props.inputProps?.step) || 1;
    const min =
      props.inputProps?.min !== undefined
        ? parseFloat(props.inputProps.min)
        : -Infinity;
    const max =
      props.inputProps?.max !== undefined
        ? parseFloat(props.inputProps.max)
        : Infinity;
    // Read what the user can actually see: the live input beats the prop, which a controlled
    // caller keeps in sync anyway but an uncontrolled one may have let drift while typing.
    const displayed = inputNodeRef.current ? inputNodeRef.current.value : defaultVal;
    const current = parseFloat(displayed);
    const from = Number.isNaN(current) ? 0 : current;
    // Decimal steps (e.g. 0.01 for £) otherwise accumulate binary float noise — 1000.0000000000001.
    const decimals = (String(step).split(".")[1] || "").length;
    const stepped = Number((from + direction * step).toFixed(decimals));
    const next = Math.min(max, Math.max(min, stepped));
    if (inputNodeRef.current) inputNodeRef.current.value = String(next);
    if (props.onChange) {
      props.onChange({
        target: { id: props.id, name: props.name, value: String(next) },
      });
    }
  };

  const handleNumericIncrement = () => stepNumericValue(1);

  const handleNumericDecrement = () => stepNumericValue(-1);

  const numericSpinner =
    isNumberType && !props.disabled && !props.readOnly ? (
      <div className={classes.spinnerWrap}>
        <IconButton
          className={classes.spinnerBtn}
          aria-label={`Increase ${props.label || "value"}`}
          onClick={handleNumericIncrement}
          onMouseDown={(e) => e.preventDefault()}
          tabIndex={-1}
          disableRipple
        >
          <ArrowDropUpIcon className={classes.spinnerIcon} />
        </IconButton>
        <IconButton
          className={classes.spinnerBtn}
          aria-label={`Decrease ${props.label || "value"}`}
          onClick={handleNumericDecrement}
          onMouseDown={(e) => e.preventDefault()}
          tabIndex={-1}
          disableRipple
        >
          <ArrowDropDownIcon className={classes.spinnerIcon} />
        </IconButton>
      </div>
    ) : null;

  const lengthExceeded = maxLength && defaultVal.length >= maxLength;

  const calculatedHelperText =
    defaultVal.length > 0
      ? lengthExceeded
        ? `You've reached the maximum character limit of ${maxLength}.`
        : maxLength
        ? `Used ${defaultVal.length} of ${maxLength} characters`
        : props.helperText
      : props.helperText;

  const calculatedError = props.error;

  return (
    <ThemeProvider theme={theme}>
      <TextField
        name={props.name}
        id={props.id}
        label={props.label}
        variant={props.variant}
        color={props.color}
        size={props.size}
        type={props.type}
        required={props.required}
        disabled={props.disabled}
        InputLabelProps={inputLabelProps}
        InputProps={{
          min: "0",
          max: "10",
          readOnly: props.readOnly,
          ...props.InputProps,
          className: `${props.InputProps?.className || ""} ${
            props.className || ""
          }`,
          endAdornment: (
            <InputAdornment position="end">
              {props.adornment?.position === "end" && (
                <span
                  style={{
                    marginRight: 8,
                    fontSize: "1rem",
                    display: "flex",
                    alignItems: "center",
                    marginTop: "-3px",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {props.adornment.value}
                </span>
              )}
              {props.InputProps?.endAdornment}
              {numericSpinner}
              <A_FormHelper fieldHelperText={props.fieldHelperText} />
            </InputAdornment>
          ),
          startAdornment:
            props.adornment?.position === "start" ? (
              <InputAdornment position="start">
                <span
                  style={{
                    fontSize: "1rem",
                    display: "flex",
                    alignItems: "center",
                    color: "var(--color-text-secondary)",
                    marginRight: "4px",
                    marginTop: "-3px",
                  }}
                >
                  {props.adornment.value}
                </span>
                {props.InputProps?.startAdornment}
              </InputAdornment>
            ) : (
              props.InputProps?.startAdornment || ""
            ),
          autoFocus: props.focus,
          [formVlalueType]: defaultVal,
        }}
        inputProps={{
          maxLength: maxLength,
          ...props.inputProps,
        }}
        helperText={calculatedHelperText}
        error={calculatedError}
        fullWidth={props.fullWidth}
        margin={props.margin}
        multiline={props.multiline}
        rows={props.rows}
        rowsMax={props.rowsMax}
        className={`${classes.root} ${props.className || ""}`}
        inputRef={setInputRef}
        onChange={handleChange}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        onKeyPress={props.onKeyPress}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </ThemeProvider>
  );
};

A_TextField.defaultProps = {
  label: "Text field",
  variant: "outlined",
  color: "primary",
  size: "small",
  borderColor: "white",
  required: false,
  disabled: false,
  readOnly: false,
  error: false,
  fullWidth: false,
  margin: "dense",
  multiline: false,
  rows: 0,
  rowsMax: 5,
  helperText: "",
  defaultValue: "",
  inputType: "",
};

A_TextField.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  adornment: PropTypes.object,
  variant: PropTypes.oneOf(["standard", "filled", "outlined"]),
  color: PropTypes.oneOf(["primary", "secondary"]),
  size: PropTypes.oneOf(["small", "medium"]),
  type: PropTypes.oneOf(["text", "password", "search", "number", "tel", "email"]),
  margin: PropTypes.oneOf(["none", "normal", "dense"]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.node,
  defaultValue: PropTypes.node,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  rowsMax: PropTypes.number,
  inputMaxLength: PropTypes.number,
  inputMaxDecimals: PropTypes.number,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
  className: PropTypes.any,
  inputType: PropTypes.string,
  focus: PropTypes.bool,
  fieldHelperText: PropTypes.string,
  InputLabelProps: PropTypes.object,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export default A_TextField;
